import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useMemo, useRef, useState } from 'react';

import { dateFormatShortTime, parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { AddStopIcon } from '../../../../assets/svg/AddStopIcon';
import { CopyStopsIcon } from '../../../../assets/svg/CopyStopsIcon';
import { RefreshIcon } from '../../../../assets/svg/RefreshIcon';
import { StopsManagementMenu } from '../../../../components/StopsManagementMenu/StopsManagementMenu';
import { getMarkerStatusColor } from '../../../../constants/Colors';
import { EventTrackTypes } from '../../../../constants/EventTracks';
import { ROUTE_START_LOCATION, ROUTE_STATUS } from '../../../../constants/Routes';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Route } from '../../../../domain/route/Route';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import Button from '../../../../react-components/Button';
import { Text } from '../../../../react-components/Text';
import { usePermissions } from '../../hooks/usePermissions';
import { useRetrieveDriverCredentials } from '../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { useRouteContext } from '../context/context';
import { BarStyle, StopPill, TagStyle } from './StopPill';
import { getStopChips } from './stop/StopChips';
import { useSetNextStopTopScroll } from './stop/hooks/useSetNextStopTopScroll';

type RouteStopsProps = {
  route: Route;
  onSelectStop?: (stopId: string) => void;
  openSearch?: () => void;
  showButtonsIfEmpty?: boolean;
};

export const StopsListContainer = ({ route, onSelectStop, openSearch, showButtonsIfEmpty = true }: RouteStopsProps) => {
  const { capture } = useCaptureEvents();
  const { isSoloDriver } = usePermissions();
  const { data: credentials } = useRetrieveDriverCredentials();
  const { routeState } = useRouteContext();
  const { data: vehicleData } = useRetrieveVehicle();
  const { nextStopTopScroll, setTopScroll } = useSetNextStopTopScroll();
  const { canManageStops: canCreateStops } = usePermissions();

  const selectedStop = useRef<HTMLDivElement | null>(null);

  const sortedStops = useMemo(() => route.getSortedStopList({}), [route]);
  const selectedPos = sortedStops.findIndex((stop) => stop.status === `pending`);
  const project = vehicleData?.project;

  const [isViewingCopyStops, setIsViewingCopyStops] = useState(false);

  useEffect(() => {
    if (selectedStop.current) {
      const value = selectedStop.current.offsetTop - 150;
      if (value !== nextStopTopScroll) setTopScroll(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStop.current]);

  if (!project || !credentials) return null;

  if (route.getNonSpecificStops().length === 0) {
    return (
      <>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          wrap="nowrap"
          style={{ padding: `5px  20px`, width: `100%`, height: `100%` }}
        >
          {showButtonsIfEmpty ? (
            <>
              <Text intlId="driver.stops.no_stops_list.title" variant="body-medium" />
              <VSpacer medium />
              <Text
                intlId={canCreateStops ? `driver.stops.no_stops_list.info.create` : `driver.stops.no_stops_list.info`}
                variant="small"
              />
              <VSpacer medium />
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                direction="column"
                wrap="nowrap"
                style={{ width: `100%` }}
              >
                {!isSoloDriver && (
                  <Button
                    onClick={() => window.location.reload()}
                    variant="flat"
                    style={{ width: `100%`, height: `52px`, padding: `4px 24px`, boxSizing: `border-box` }}
                  >
                    <RefreshIcon />
                    <HSpacer small />
                    <Text intlId="driver.stops.no_stops_list.refresh_page" />
                  </Button>
                )}
                {canCreateStops && (
                  <Button
                    onClick={() => {
                      openSearch && openSearch();
                    }}
                    style={{ width: `100%`, height: `52px`, padding: `4px 24px`, boxSizing: `border-box` }}
                  >
                    <AddStopIcon />
                    <HSpacer small />
                    <Text intlId="driver.stops.no_stops_list.create_stops" style={{ color: RoutalPalette.white }} />
                  </Button>
                )}
                {isSoloDriver && (
                  <Button
                    onClick={() => {
                      capture({
                        type: EventTrackTypes.CopyStops,
                        payload: {
                          [`Action`]: `Open`,
                          [`From`]: `Empty route`,
                        },
                      });
                      setIsViewingCopyStops(true);
                    }}
                    styleType="secondary"
                    style={{
                      width: `100%`,
                      height: `52px`,
                      padding: `4px 24px`,
                      boxSizing: `border-box`,
                    }}
                  >
                    <CopyStopsIcon />
                    <HSpacer small />
                    <Text
                      intlId="driver.stops.no_stops_list.copy_stops_other_route"
                      style={{ color: RoutalPalette.white }}
                    />
                  </Button>
                )}
              </Grid>
            </>
          ) : (
            <Text intlId="driver.stops.no_stops_list.title.no_buttons" variant="body-medium" />
          )}
        </Grid>
        <StopsManagementMenu
          isOpen={isViewingCopyStops}
          selectedRoute={route}
          onClose={() => setIsViewingCopyStops(false)}
          onComplete={() => {
            capture({
              type: EventTrackTypes.CopyStops,
              payload: {
                Action: `Copy`,
                From: `Empty route`,
              },
            });
          }}
          mode="copy"
        />
      </>
    );
  }

  return (
    <Grid style={{ padding: `5px  0px`, height: `fit-content`, width: `100%` }}>
      {sortedStops.map((stop, i) => {
        // We init the generic values of the stop
        const isFirstItem = i === 0;
        const isLastItem = i === route.getAllStops().length - 1;

        let specialLabel = undefined;
        if (isFirstItem && credentials.test(`can_view_planning_times`)) {
          if (route.status === ROUTE_STATUS.notStarted && route.planned_start_time) {
            const date = parseSecondsToHM(route.planned_start_time, true);
            specialLabel = intl.formatMessage({ id: `drivers.stop.pill.route_planned_starts_at` }, { time: date });
          }
          // The status check is for consistency and for testing from planner
          if (route.status !== ROUTE_STATUS.notStarted && route.start_date) {
            const date = dateFormatShortTime(route.start_date);
            specialLabel = intl.formatMessage({ id: `drivers.stop.pill.route_started_at` }, { time: date });
          }
        }
        if (isLastItem && credentials.test(`can_view_planning_times`)) {
          if (route.status === ROUTE_STATUS.notStarted && route.planned_end_time) {
            const date = parseSecondsToHM(route.planned_end_time, true);
            specialLabel = intl.formatMessage({ id: `drivers.stop.pill.route_planned_ends_at` }, { time: date });
          }
          // The status check is for consistency and for testing from planner
          if (route.status !== ROUTE_STATUS.notStarted && route.estimated_end_time) {
            const date = dateFormatShortTime(route.estimated_end_time);
            specialLabel = intl.formatMessage({ id: `drivers.stop.pill.stop_estimated_arrival_at` }, { time: date });
          }
        }
        // We init the styles
        let botStyle: BarStyle = `unhighlighted`;
        let topStyle: BarStyle = `unhighlighted`;
        let tagStyle: TagStyle = `unhighlighted`;

        let background = undefined;

        // We check the styles
        if (i <= selectedPos && route.status !== `not_started`) {
          botStyle = topStyle = `highlighted`;
        }
        if (
          (i === selectedPos && route.status !== `not_started`) ||
          (route.status === ROUTE_STATUS.notStarted && stop.id === ROUTE_START_LOCATION)
        ) {
          botStyle = `unhighlighted`;
          tagStyle = `highlighted`;
          background = RoutalPalette.grey6;
        }

        let color = RoutalPalette.routeBaseColor;
        let secondaryColor = undefined;
        if (
          stop.status === STOP_STATUS.completed ||
          stop.status === STOP_STATUS.canceled ||
          stop.status === STOP_STATUS.incomplete
        ) {
          tagStyle = `highlighted`;
          botStyle = topStyle = `highlighted`;
          color = getMarkerStatusColor(stop.status);
          secondaryColor = getMarkerStatusColor(stop.status, `light`);
        }

        if (isFirstItem) {
          topStyle = `void`;
        }
        if (isLastItem) {
          botStyle = `void`;
        }

        const inProgress = routeState.startedReportStop === stop.id;

        const stopChips = getStopChips({ stop, report: stop.report, color, project });

        return (
          <StopPill
            ref={selectedPos === i ? selectedStop : undefined}
            key={stop.id}
            stop={stop}
            stopType={stop.type}
            componentColor={color as string}
            secondaryColor={secondaryColor as string}
            botStyle={botStyle as BarStyle}
            topStyle={topStyle as BarStyle}
            tagStyle={tagStyle as TagStyle}
            onSelect={onSelectStop ? (stopId: string) => onSelectStop(stopId) : undefined}
            additionalChips={stopChips}
            showCustomerField={!!stop.customer_text_field}
            showInProgress={inProgress}
            showOptimizationWarning={stop.optimization_warning}
            done={
              stop.status === STOP_STATUS.completed ||
              stop.status === STOP_STATUS.canceled ||
              stop.status === STOP_STATUS.incomplete
            }
            containerStyles={{ backgroundColor: background, padding: `0 20px 0px 20px` }}
            specialLabel={specialLabel}
          />
        );
      })}
    </Grid>
  );
};
